// © Microsoft Corporation. All rights reserved.
import { AppType, ExperienceTypes, RiverConfigType, WebWorkerMessageId, environment, logger, notifySubscription, registerCommandMessageHandler, sendMessage, webWorkerCache } from "../wwCommon";
import { ContentPreviewFetchFailed, UnknownComplexInfopaneSlide } from "../AppErrors";
import { experienceConfigMap, getExperienceTypeConfig } from "../wwResolver";
import { fetchImage, imageSizeMap } from "./Image";
import { OneServiceResponseSchema } from "./Common";
import { ContentPreview } from "./Constants";
import { IndexedDBCache } from "../wwCommon";
import { get } from "lodash-es";
let contentPreviewCache;
let isRefreshComplete = false;
// Are we overriding the default image quality? Exported only for testing
export let overrideImageQuality;
/** Registers content preview related tasks */
export function registerContentPreviewTasks() {
    if (isFeatureEnabled()) {
        initializecontentPreviewCache();
        registerCommandMessageHandler(WebWorkerMessageId.PostTtvr, refreshStaleContentPreviewItems);
    }
    const myFeedSubscription = webWorkerCache.subscribe(WebWorkerMessageId.MyFeed, feedResponse => {
        myFeedSubscription.unsubscribe();
        fetchItems(feedResponse, WebWorkerMessageId.MyFeed);
    });
    const stripeMyFeedSubscription = webWorkerCache.subscribe(WebWorkerMessageId.StripeMyFeed, stripeFeedResponse => {
        stripeMyFeedSubscription.unsubscribe();
        fetchItems(stripeFeedResponse, WebWorkerMessageId.StripeMyFeed);
    });
}
/**
 * Fetches content preview items or only images based on feed response schema
 * @param feedResponse - the feed response
 * @param messageId - web worker message id
 */
function fetchItems(feedResponse, messageId) {
    if (!feedResponse || !feedResponse.payload || !feedResponse.payload.feedData) {
        return;
    }
    const { responseSchema, feedData, isTabbedInfopaneResponse } = feedResponse.payload;
    let feedItems;
    if (responseSchema === OneServiceResponseSchema.CardView) {
        feedItems = isTabbedInfopaneResponse ? feedData[0] && feedData[0].subCards : feedData.subCards;
    }
    else {
        feedItems = feedData.feedItems;
    }
    if (!feedItems || !feedItems.length) {
        return;
    }
    const contentPreviewItems = getContentItems(feedItems, messageId);
    const contentItems = filterCachedItems(contentPreviewItems.infopaneContentItems);
    const riverContentItems = filterCachedItems(contentPreviewItems.contentItems);
    // Add content preview items for river.
    const contentPreviewPreloadCount = Math.min(getWWContentPreviewPreloadCount(messageId), riverContentItems.length);
    for (let index = 0; index < contentPreviewPreloadCount; index++) {
        contentItems.push(riverContentItems[index]);
    }
    if (!contentItems.length) {
        return;
    }
    // Card view response contains card content, only fetch images
    if (responseSchema === OneServiceResponseSchema.CardView) {
        fetchImages(contentItems);
        return;
    }
    fetchContentPreviewItems(contentItems);
}
/**
 * Initializes content preview cache
 */
function initializecontentPreviewCache() {
    contentPreviewCache = new IndexedDBCache({
        dbName: ContentPreview.dbName,
        storeName: ContentPreview.storeName,
        indexName: ContentPreview.indexName,
        freshnessDuration: 5 * 60 * 1000 // 5 minutes
    });
    // Check if we are overriding the quality of resized images.
    overrideImageQuality = false;
    const contentPreviewConfigSubscription = experienceConfigMap.subscribe(ExperienceTypes.ContentPreview, (cpConfig) => {
        contentPreviewConfigSubscription.unsubscribe();
        overrideImageQuality = get(cpConfig, "properties.overrideDefaultImageQuality");
    });
    contentPreviewCache.initialize().then(() => {
        const contentPreviewItems = contentPreviewCache.toArray();
        webWorkerCache.set(WebWorkerMessageId.ContentPreviewData, { payload: contentPreviewItems });
        sendMessage({ id: WebWorkerMessageId.ContentPreviewData, fetched: true, payload: contentPreviewItems });
    });
    // Perform cleanup irrespective of PostTtvr message
    setTimeout(refreshStaleContentPreviewItems, 5000);
}
/**
 * Gets multiple content preview items
 * @param contentItems - content items to fetch
 */
function fetchContentPreviewItems(contentItems) {
    if (!contentItems || !contentItems.length) {
        return;
    }
    return Promise.all(contentItems.map(item => fetchContentPreviewItem(item))).then(() => {
        if (contentPreviewCache) {
            contentPreviewCache.saveToIndexedDB();
            notifySubscription(WebWorkerMessageId.ContentPreviewCache, contentPreviewCache.toArray());
        }
    });
}
/**
 * Fetches images for card view response
 * @param feedItems - the feed items from response
 */
function fetchImages(contentItems) {
    contentItems.map(item => fetchImage(item.imageMetadata, item.imageSize, { appErrorPB: { parentId: item.id } }));
}
/**
 * Refreshes stale content preview items
 */
function refreshStaleContentPreviewItems() {
    if (isRefreshComplete || !contentPreviewCache || !contentPreviewCache.getStaleItems().length) {
        return;
    }
    isRefreshComplete = true;
    // Use Map to avoid duplicates
    const staleItems = new Map();
    findStaleItemsInCache(WebWorkerMessageId.MyFeed, staleItems);
    findStaleItemsInCache(WebWorkerMessageId.StripeMyFeed, staleItems);
    if (staleItems.size === 0) {
        return;
    }
    const contentPreviewPromises = [];
    staleItems.forEach((item) => {
        contentPreviewPromises.push(fetchContentPreviewItem(item));
    });
    Promise.all(contentPreviewPromises).then(() => {
        contentPreviewCache.saveToIndexedDB();
        notifySubscription(WebWorkerMessageId.ContentPreviewCache, contentPreviewCache.toArray());
    });
}
/**
 * Finds stale items in content preview cache that are part of current feed response
 * @param messageId - the message Id to determine MyFeed and StripeMyFeed feed responses
 * @param staleItems - the staleItems Map object used to check for duplicates
 */
function findStaleItemsInCache(messageId, staleItems) {
    const cacheEntry = webWorkerCache.get(messageId);
    if (!cacheEntry
        || !cacheEntry.payload
        || cacheEntry.payload.responseSchema === OneServiceResponseSchema.CardView
        || !cacheEntry.payload.feedData) {
        return;
    }
    const feedItems = cacheEntry.payload.feedData.feedItems;
    if (!feedItems || !feedItems.length) {
        return;
    }
    // Get all content preview items from feed response
    const riverItems = getContentItems(feedItems, null, true);
    const contentItems = [...(riverItems.contentItems || []), ...(riverItems.infopaneContentItems || [])];
    const staleItemsInCache = contentPreviewCache.getStaleItems().map(item => item.id);
    contentItems.forEach((item) => {
        if (!staleItems.has(item.id) && staleItemsInCache.indexOf(item.id) !== -1) {
            staleItems.set(item.id, item);
        }
    });
}
/**
 * Makes fetch call for content preview and marks cache state as fetching item.
 * @param item - The item metadata
 */
function fetchContentPreviewItem(item) {
    const { id, variation, locale } = item;
    let url;
    if (variation && locale) {
        const search = environment.ContentServiceUrlBase.search
            ? environment.ContentServiceUrlBase.search + `&market=${locale}`
            : `?market=${locale}`;
        url = new URL(`view/v1/Preview/${id}${variation}${search}`, environment.ContentServiceUrlBase);
    }
    else {
        url = new URL(`view/v1/Preview/${id}${environment.ContentServiceUrlBase.search}`, environment.ContentServiceUrlBase);
    }
    return fetch(url.href).then(response => {
        if (!response.ok) {
            throw `ContentPreview call failed with status [${response.status}].`;
        }
        return response.json();
    }).then(data => {
        onFetchComplete(item, data);
    }).catch(reason => {
        logger.error(Object.assign(Object.assign({}, ContentPreviewFetchFailed), { message: "Content Preview fetch failed", pb: Object.assign(Object.assign({}, ContentPreviewFetchFailed.pb), { customMessage: `Error: ${reason}. Url: ${url}` }) }));
        onFetchComplete(item);
    });
}
/**
 * Marks cache state complete for given item id. Responds to any pending messages. And the image fetch.
 * @param item - The item metadata
 * @param response - The response
 */
function onFetchComplete(item, response = null) {
    const { id, imageSize } = item;
    webWorkerCache.set(id, { payload: response });
    logger.info(`WebWorker.fetchContentPreviewComplete: [id ${id}]`);
    if (!response) {
        return;
    }
    if (contentPreviewCache) {
        contentPreviewCache.set(id, response);
    }
    if (response.images && response.images.length) {
        fetchImage(response.images[0], imageSize, { appErrorPB: { parentId: id }, overrideImageQuality });
    }
}
/**
 * Get metadata for all the content items.
 * @param feedItems - The response
 * @param messageId - web worker message id
 * @param getAllInfoPaneItems - Boolean to return all infopane items, by default only the first infopane item is returned
 * @returns An array of ContentItem
 */
function getContentItems(feedItems, messageId = null, getAllInfoPaneItems = false) {
    if (!feedItems || !feedItems.length) {
        return {};
    }
    const contentPreviewItems = {};
    const contentItems = [];
    let fetchDoubleWideImageForMMX = true;
    const isMMX = environment.apptype === AppType.MMX;
    // External link items in river (with type = 'link') don't need to be fetched so skip those
    feedItems
        .filter(item => item.type !== "link")
        .forEach((item) => {
        if (item.type === "infopane") {
            let subItems = item.subItems || item.subCards;
            // External link items in infopane (with type = 'link') don't need to be fetched so skip those
            subItems = subItems.filter(subItem => subItem.type !== "link");
            contentPreviewItems.infopaneContentItems = getInfopaneContentItems(getAllInfoPaneItems ? subItems : [subItems[0]]);
        }
        else if (item.id) {
            let imageSize = messageId === WebWorkerMessageId.StripeMyFeed ? imageSizeMap.Image_1x1y : imageSizeMap.ContentPreview;
            const { locale, variation, images = [] } = item.metadata || item;
            if (isMMX) {
                if (fetchDoubleWideImageForMMX) {
                    // Pre-fetch the first image on MMX as a double-wide
                    imageSize = imageSizeMap.HalfPane;
                    fetchDoubleWideImageForMMX = false;
                }
                else {
                    // Don't pre-fetch images
                    imageSize = null;
                }
            }
            contentItems.push({
                id: item.id,
                imageSize,
                locale,
                variation,
                imageMetadata: images[0]
            });
        }
        else {
            logger.info(`WebWorker.getContentItems: Skipped fetching content item of type ${item.type}`);
        }
    });
    contentPreviewItems.contentItems = contentItems;
    return contentPreviewItems;
}
/**
 * Gets the Infopane content preview items
 * @param items - infoPane items from feed response
 */
function getInfopaneContentItems(items) {
    let contentItems = [];
    if (!items || items.length === 0) {
        return contentItems;
    }
    items.forEach(item => {
        if (item.id) {
            const { locale, variation, images = [] } = item.metadata || item;
            contentItems.push({
                id: item.id,
                imageSize: imageSizeMap.HalfPane,
                locale,
                variation,
                imageMetadata: images[0]
            });
        }
        else {
            const complexSlideContentIds = getInfopaneComplexSlideContentItems(item.type, item.subItems);
            contentItems = [...contentItems, ...complexSlideContentIds];
        }
    });
    return contentItems;
}
/**
 * Get metadata for Infopane complex slide content items.
 * @param type - The complex slide type
 * @param items - The complex slide items
 * @returns An array of ContentItem
 */
function getInfopaneComplexSlideContentItems(type, items) {
    const contentItems = [];
    const sizeMap = imageSizeMap[type];
    if (!sizeMap) {
        logger.error(Object.assign(Object.assign({}, UnknownComplexInfopaneSlide), { message: `Unknown Infopane complex slide type ${type}` }));
        return contentItems;
    }
    items.forEach((item, index) => {
        if (!item.id || item.type === "link") {
            return;
        }
        const { locale, variation, images = [] } = item.metadata || item;
        contentItems.push({
            id: item.id,
            imageSize: sizeMap[index],
            locale,
            variation,
            imageMetadata: images[0]
        });
    });
    return contentItems;
}
/**
 * Filters out items that are already cached
 * @param items the list of items to filter
 * @returns items not cached
 */
function filterCachedItems(items) {
    if (!items || !items.length) {
        return [];
    }
    if (!contentPreviewCache) {
        return items;
    }
    return items.filter((contentItem) => !contentPreviewCache.has(contentItem.id));
}
/**
 * get the max number of content preview items to preload.
 * preloadRiverContentItemsInWebWorker is used from River config.
 * If not present in river config, it assumes 1 for MMX and 12 for non MMX.
 * For debugging, it can be overwritten from the query string param wwcontentpreviewpreloadcount
 * @param messageId - web worker message id
 * @returns {number}
 */
function getWWContentPreviewPreloadCount(messageId) {
    const param = environment.getQueryParameter("previewcount");
    if (param) {
        const count = Number(param);
        if (!isNaN(count)) {
            return count;
        }
    }
    const experienceType = messageId === WebWorkerMessageId.StripeMyFeed ? RiverConfigType.StripeMyFeed : RiverConfigType.MyFeed;
    const config = getExperienceTypeConfig(experienceType);
    const preloadRiverContentItemsInWebWorker = config &&
        get(config, "properties.cardProviderConfig.initialRequest.preloadRiverContentItemsInWebWorker");
    if (environment.apptype === AppType.MMX) {
        return preloadRiverContentItemsInWebWorker || 1;
    }
    if (preloadRiverContentItemsInWebWorker) {
        return preloadRiverContentItemsInWebWorker;
    }
    return messageId === WebWorkerMessageId.StripeMyFeed ? 3 : 12;
}
/**
 * Checks if content preview cache feature is enabled
 */
function isFeatureEnabled() {
    if (environment.disableContentPreviewCache === true) {
        return false;
    }
    return environment.apptype === AppType.EdgeChromium;
}
//# sourceMappingURL=ContentPreview.js.map